<template>
    <b-container fluid v-if="invoice" style="padding-left: 120px;padding-right: 120px;">
        <b-row align-h="start">
            <b-col cols="3">
                <font-awesome-icon icon="file-invoice" size="lg" style="float: left;" />
                <h3>{{$t('createInvoice.label.CreateInvoice')}}</h3>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <editAddress
                    :title="$t('createInvoice.label.SenderAddress')"
                    prefix="Senders"
                    :v="$v.invoice.senderAddress"
                    :address="invoice.senderAddress"
                    :addressBook="computedAddressBook"
                    :addressBookType="'AddressBook'"
                    :dynamicWidth="true"
                />
            </b-col>
            <b-col>
                <editAddress
                    :title="$t('createInvoice.label.DeliveryAddress')"
                    prefix="Delivery"
                    :v="$v.invoice.deliverAddress"
                    :address="invoice.deliverAddress"
                    :addressBook="computedAddressBook"
                    :addressBookType="'AddressBook'"
                    :dynamicWidth="true"
                />
            </b-col>
            <b-col>
                <editAddress
                    :title="$t('createInvoice.label.BillingAddress')"
                    prefix="Billing"
                    :v="$v.invoice.billingAddress"
                    :address="invoice.billingAddress"
                    :addressBook="computedAddressBook"
                    :addressBookType="'SoldTo'"
                    :dynamicWidth="true"
                />
            </b-col>
        </b-row>
        <b-row class="mt-4">
            <b-col>
                <editInvoiceDetails
                    :invoiceDetails="invoice.invoiceDetails"
                    :v="$v.invoice.invoiceDetails"
                ></editInvoiceDetails>
            </b-col>
        </b-row>
        <b-row class="mt-4">
            <b-col sm="12">
                <editCommodityDetails :invoice="invoice" :v="$v"></editCommodityDetails>
            </b-col>
        </b-row>
        <b-row class="mt-4">
          <p style="color: red">{{validationError}}</p>
        </b-row>
        <b-row align-h="end" class="mt-4">
            <b-col cols="2" v-if="cancelInvoice">
                <b-button @click="cancelInvoice" name="cancelInvoiceButton"
                    >{{$t('createInvoice.button.CancelInvoice')}}</b-button
                >
            </b-col>
            <b-col cols="2">
                <b-button @click="createInvoice" name="createInvoiceButton"
                    >{{$t('createInvoice.button.CreateInvoice')}}</b-button
                >
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import editAddress from '../components/CreateInvoice/editAddress'
import editInvoiceDetails from '../components/CreateInvoice/editInvoiceDetails';
import editCommodityDetails from '../components/CreateInvoice/editCommodityDetails';
import invoiceValidationMixin from '../mixins/invoiceValidationMixin';
import { CORE_PREFIX, ACTIONS, MUTATIONS, HISTORY_PREFIX, CREATE_SHIPMENT_PREFIX } from '@/store/constants';
import 'vue-select/dist/vue-select.css';
import {mapGetters} from "vuex";
import helper from "@/helper";
import {yoyoHttp} from "@/services/http.service";

export default {
    components: {
        editAddress,
        editInvoiceDetails,
        editCommodityDetails
    },
    mixins: [invoiceValidationMixin],
    props: {
      shipment: {type: Object},
      createOnDone: {type: Boolean, default: true},
      cancelInvoice: {type: Function, required: false},
      addressBook: { type: Array, default: () => [] },
      addressBookType: { type: String, default: null }
    },
    data() {
        return {
            id: null,
            invoice: null,
            validationError: ""
        };
    },
    watch: {
        $route: 'fetchInvoice',
      invoice: {
        deep: true,
        immediate: true,
        handler(val, oldVal) {
          this.validationError = ''
        }
      }
    },
    async mounted() {
      // Initialize the system
      await this.$store.dispatch(CORE_PREFIX + ACTIONS.CORE.INITIALIZE_SYSTEM);
  
      // Fetch the invoice first
      await this.fetchInvoice();

      //set private flags for javascript validation
      if(this.shipment && this.shipment.receiverAddress.isPrivate){
        this.invoice.deliverAddress.isPrivate = true;
        this.invoice.billingAddress.isPrivate = true;
      }
      // Fetch the AddressBook after the invoice to ensure filtering by companyId
      if (this.invoice && this.invoice.companyId) {
        await this.$store.dispatch(
            CREATE_SHIPMENT_PREFIX + ACTIONS.CREATESHIPMENT.FETCH_ADDRESS_BOOK,
            this.invoice.companyId // Pass the companyId explicitly
        );
      }
  
      // Disable loading after all fetches are complete
      this.$store.dispatch(CORE_PREFIX + ACTIONS.CORE.SET_LOADING, false);
    },

    methods: {
        async (){

        },
        async fetchInvoice() {
          const shipmentId = this.$route.params.shipmentId || this.$route.query.id || (this.shipment && this.shipment.shipmentId);

          let enrichedInvoice = null;
          if (shipmentId) {
            enrichedInvoice = await this.$store.dispatch(
                CREATE_SHIPMENT_PREFIX + ACTIONS.HISTORY.GET_INVOICE_DATA,
                shipmentId
            );
          }

          // Fetch the base invoice
          if (this.$route.params.shipmentId || this.$route.params.linkId) {
            this.invoice = await this.$store.dispatch(
                HISTORY_PREFIX + ACTIONS.HISTORY.FETCH_INVOICE,
                {shipmentId: this.$route.params.shipmentId, linkId: this.$route.params.linkId}
            );
          } else if (!this.invoice) {
            this.invoice = await this.$store.dispatch(
                HISTORY_PREFIX + ACTIONS.HISTORY.FETCH_INVOICE,
                null
            );

            // If the shipment was passed in as a prop, map it to invoice
            if (this.shipment) {
              const cloneShipment = {...this.shipment};
              if (this.shipment.receiverPays != null) {
                this.shipment.insuranceAmount = null;
              }
              this.shipment.accountId = this.getSelectedCompanyAccount.id;
              if (this.shipment.shipmentOptions.incoterms == null) {
                delete this.shipment.shipmentOptions.incoterms;
              }
              const invoiceData = await yoyoHttp.post('/api/Print/MapInvoiceData', this.shipment);
              this.invoice = invoiceData.data;

              if (this.shipment.receiverPays != null) {
                this.shipment.insuranceAmount = cloneShipment.insuranceAmount;
              }

              if(this.shipment.ColliDetails && this.shipment.ColliDetails.NumberOfColli){
                this.invoice.InvoiceDetails.ColliCount = this.shipment.ColliDetails.NumberOfColli;
              }
            }
          }

          // ✅ Enrich now, after this.invoice is defined
          if (
              enrichedInvoice &&
              this.invoice &&
              Array.isArray(this.invoice.invoiceLines) &&
              Array.isArray(enrichedInvoice.invoiceLines)
          ) {
            const fieldsToCopy = [
              'commodity',
              'harmonizedCode',
              'countryOfOrigin',
              'itemNumber',
              'manufacturer',
              'quantity',
              'unitType',
              'unitPrice'
            ];

            enrichedInvoice.invoiceDetails.MeasurementSystem = 0;
            if (this.getShipmentSystem === 'YOYOUnitedStates') {
              enrichedInvoice.invoiceDetails.MeasurementSystem = 1;
            }

            if(this.shipment.ColliDetails && this.shipment.ColliDetails.NumberOfColli){
              this.invoice.InvoiceDetails.ColliCount = this.shipment.ColliDetails.NumberOfColli;
            } else {
              this.invoice.InvoiceDetails = enrichedInvoice.InvoiceDetails;
            }

            enrichedInvoice.invoiceLines.forEach((enrichedLine, index) => {
              // If this line doesn't exist in the current invoice, create it
              if (!this.invoice.invoiceLines[index]) {
                this.invoice.invoiceLines.push({});
              }

              const targetLine = this.invoice.invoiceLines[index];

              fieldsToCopy.forEach(field => {
                if (enrichedLine[field]) {
                  targetLine[field] = enrichedLine[field];
                }
              });

              // Carry over HsCode requirement if relevant
              if (enrichedLine.requireHsCode) {
                targetLine.requireHsCode = true;
              }
            });

            // Optional: ensure all fields are initialized
            this.invoice.invoiceLines.forEach(line => {
              fieldsToCopy.forEach(field => {
                if (line[field] === undefined) {
                  line[field] = '';
                }
              });

              if (line.quantity === undefined) line.quantity = 0;
              if (line.unitPrice === undefined) line.unitPrice = 0;
            });
          }
        },
        async createInvoice() {
            this.$v.invoice.$touch();
            if (this.$v.invoice.$invalid) {
                return;
            }
            
            if (!this.createOnDone){
              this.$emit('hide', this.invoice);
              return;
            }

            this.invoice.invoiceDetails.MeasurementSystem = 0;

            if (this.getShipmentSystem === 'YOYOUnitedStates') {
              this.invoice.invoiceDetails.MeasurementSystem = 1;
            }

            const response = await this.$store.dispatch(
                HISTORY_PREFIX + ACTIONS.HISTORY.CREATE_INVOICE,
                {invoice: this.invoice}
            );

            window.open(response, '_blank');
        },
    },
    computed:{
      ...mapGetters('createShipment', ['getShipment', 'getSelectedCompanyAccount','getAddressBook']),
        computedAddressBook() {
          return this.getAddressBook || [];
        },
      ...mapGetters('core', ['getListOfTransportProducts']),
      addressBook() {
        return this.getAddressBook || [];
      },
      showEuriNumber(type){
        if(this.invoice.billingAddress.countryCode === 'GB')
          return true;
        if(this.invoice.senderAddress.countryCode === 'GB')
          return true;
        if(this.invoice.deliverAddress.countryCode === 'GB')
          return true;
        return false;
      },
    }
};
</script>
